import type { I18nString } from "./common";
import { ArticleType } from "./enums";

export interface AuthorSNS {
  platformName: string;
  link: I18nString;
}

export interface AuthorInfo {
  name: I18nString;
  profileImageURL?: I18nString;
  socialAccounts?: AuthorSNS[];
  biography?: I18nString;
}

export interface ArticleThumbnail {
  url: I18nString;
  alt: I18nString;
}

export interface ArticleBase {
  _id: string;
  type: ArticleType;
  title: I18nString;
  excerpt: I18nString;
  thumbnail?: ArticleThumbnail;
  authorInfo: AuthorInfo;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogArticle extends ArticleBase {
  type: ArticleType.BLOG;
  url: I18nString;
}

export interface PremiumArticle extends ArticleBase {
  type: ArticleType.PREMIUM;
  content: I18nString;
  freeContent: I18nString;
  sourceFileId?: string;
  sourceFileUpdatedAt?: string;
}

export type Article = BlogArticle | PremiumArticle;

export function isBlogArticle(article: Article): article is BlogArticle {
  return article.type.toUpperCase() === ArticleType.BLOG;
}

export function isPremiumArticle(article: Article): article is PremiumArticle {
  return article.type.toUpperCase() === ArticleType.PREMIUM;
}

// List item (fewer fields than full Article)
export interface AdminArticleListItem {
  _id: string;
  type: ArticleType;
  title: I18nString;
  excerpt: I18nString;
  thumbnail?: ArticleThumbnail;
  authorInfo: AuthorInfo;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// Author file types (file-based content flow)
export interface AdminAuthorFileItem {
  _id: string;
  title: string;
  updatedAt: string;
  isPublished: boolean;
  articleId?: string;
  hasUpdate: boolean;
}

export interface AdminAuthorFileDetail {
  _id: string;
  title: string;
  content: string;
  updatedAt: string;
}

// Filters
export interface AdminArticleFilters {
  searchQuery?: string;
  type?: ArticleType;
  publishedOnly?: boolean;
}

export interface AdminAuthorFilesFilters {
  search?: string;
}

// Mutation inputs
export interface CreateBlogArticleInput {
  type: ArticleType.BLOG;
  title: I18nString;
  excerpt: I18nString;
  url: I18nString;
  thumbnail?: ArticleThumbnail;
  authorInfo: AuthorInfo;
}

export interface CreateArticleFromFileInput {
  fileId: string;
  excerpt: I18nString;
  thumbnail?: ArticleThumbnail;
  authorInfo: AuthorInfo;
  freeContentHtml: string;
  publishNow?: boolean;
}

export interface UpdateArticleInput {
  articleId: string;
  title?: I18nString;
  excerpt?: I18nString;
  url?: I18nString;
  content?: I18nString;
  freeContent?: I18nString;
  thumbnail?: ArticleThumbnail;
  authorInfo?: AuthorInfo;
}

export interface UpdateArticleMetaInput {
  articleId: string;
  excerpt?: I18nString;
  thumbnail?: ArticleThumbnail;
  authorInfo?: AuthorInfo;
  freeContentHtml?: string;
}

export interface SyncArticleContentInput {
  articleId: string;
}
